import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { prepareCustomHeaders } from "../utils/baseQueryHeaders";

type CreateChatRequest = {
  name?: string;
  /**
   * Scopes the chat to a generated text block: it stays out of the standalone
   * chat page's list and can be picked up again after a reload.
   */
  text_block_id?: string;
  messages?: any[] | null;
  model_name?: string | null;
  config_overwrite?: Record<string, any> | null;
  use_web_search?: boolean;
};

type CreateChatResponse = {
  id: number;
  model_name: string;
  messages: any[];
};

type DeleteChatRequest = {
  chat_id: number;
};

type DeleteChatResponse = {
  chat_id: number;
};

type CreateMessageRequest = {
  chat_id: number;
  text: string;
  model_name: string | null;
  image_urls?: string[] | null;
  uploaded_pdf_files?: File[] | null;
  overwrite_config?: Record<string, any> | null;
  tools?: string[];
};

type CreateMessageResponse = {
  created_message: any;
  chat_id: string;
  value_error: string | null;
  error: string | null;
};

/** A refinement chat already on the server, keyed to the text block it refines. */
export type ProductChat = {
  id: number;
  text_block_id: string;
  model_name: string;
  messages: {
    role: string;
    text: string;
    timestamp: string | null;
    html?: string;
  }[];
};

export const chatApi = createApi({
  reducerPath: "chatApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/_internal/gpt/chat",
    prepareHeaders: prepareCustomHeaders,
  }),
  tagTypes: ["Chat"],
  endpoints: (build) => ({
    createChat: build.mutation<CreateChatResponse, CreateChatRequest>({
      query: (body) => ({
        url: "/create",
        method: "POST",
        body,
      }),
    }),
    getChatsForProduct: build.query<ProductChat[], string>({
      query: (productId) => ({ url: `/for-product/${productId}` }),
    }),
    deleteChat: build.mutation<DeleteChatResponse, DeleteChatRequest>({
      query: (body) => ({
        url: `/delete/${body.chat_id}`,
        method: "DELETE",
      }),
    }),
    createMessage: build.mutation<CreateMessageResponse, CreateMessageRequest>({
      query: ({
        uploaded_pdf_files,
        chat_id,
        text,
        model_name,
        image_urls,
        overwrite_config,
        tools,
      }) => {
        const files = uploaded_pdf_files?.filter(Boolean) ?? [];
        const pdfFiles = files.filter((f) =>
          f.name?.toLowerCase().endsWith(".pdf")
        );
        const urls = (image_urls ?? []).filter((u) => u != null && u !== "");
        if (pdfFiles.length === 0) {
          return {
            url: "/message",
            method: "POST",
            body: {
              chat_id,
              text,
              model_name,
              ...(urls.length > 0 ? { image_urls: urls } : {}),
              ...(overwrite_config != null ? { overwrite_config } : {}),
              ...(tools?.length ? { tools } : {}),
            },
          };
        }
        const formData = new FormData();
        formData.append("chat_id", String(chat_id));
        formData.append("text", text);
        if (model_name != null) {
          formData.append("model_name", model_name);
        }
        if (urls.length > 0) {
          formData.append("image_urls", JSON.stringify(urls));
        }
        if (overwrite_config != null) {
          formData.append("overwrite_config", JSON.stringify(overwrite_config));
        }
        if (tools?.length) {
          formData.append("tools", JSON.stringify(tools));
        }
        for (const file of pdfFiles) {
          formData.append("file", file, file.name);
        }
        return {
          url: "/message",
          method: "POST",
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useCreateChatMutation,
  useDeleteChatMutation,
  useCreateMessageMutation,
  useGetChatsForProductQuery,
} = chatApi;
